import { useCallback, useMemo } from "react";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { FilterCarTypeProps } from "./FilterCarType";
import { CarTypeFilter } from "~core-cars/types";

export const useFilterCarType = ({
  options,
  selectedValues,
  onChange,
}: FilterCarTypeProps) => {
  const { t } = useI18nContext();
  const getLabel = useCallback(
    (value: CarTypeFilter) => {
      switch (value) {
        case "CompactCar":
          return t("core-cars.filters.type.options.CompactCar");
        case "CompactSUV":
          return t("core-cars.filters.type.options.CompactSUV");
        case "Convertible":
          return t("core-cars.filters.type.options.Convertible");
        case "EconomyCar":
          return t("core-cars.filters.type.options.EconomyCar");
        case "EconomySUV":
          return t("core-cars.filters.type.options.EconomySUV");
        case "Electric":
          return t("core-cars.filters.type.options.Electric");
        case "FullSizeCar":
          return t("core-cars.filters.type.options.FullSizeCar");
        case "FullSizeSUV":
          return t("core-cars.filters.type.options.FullSizeSUV");
        case "Hybrid":
          return t("core-cars.filters.type.options.Hybrid");
        case "LuxuryCar":
          return t("core-cars.filters.type.options.LuxuryCar");
        case "LuxurySUV":
          return t("core-cars.filters.type.options.LuxurySUV");
        case "MidSizeCar":
          return t("core-cars.filters.type.options.MidSizeCar");
        case "MidSizeSUV":
          return t("core-cars.filters.type.options.MidSizeSUV");
        case "MiniCar":
          return t("core-cars.filters.type.options.MiniCar");
        case "MinivanVan":
          return t("core-cars.filters.type.options.MinivanVan");
        case "PickupTruck":
          return t("core-cars.filters.type.options.PickupTruck");
        case "PremiumCar":
          return t("core-cars.filters.type.options.PremiumCar");
        case "PremiumSUV":
          return t("core-cars.filters.type.options.PremiumSUV");
        case "RecreationalVehicle":
          return t("core-cars.filters.type.options.RecreationalVehicle");
        case "SportsCar":
          return t("core-cars.filters.type.options.SportsCar");
        case "StandardCar":
          return t("core-cars.filters.type.options.StandardCar");
        case "StandardSUV":
          return t("core-cars.filters.type.options.StandardSUV");
        case "MysteryCar":
          return t("core-cars.filters.type.options.MysteryCar");
        default:
          return ensureExhaustive(value, null);
      }
    },
    [t]
  );

  const items = useMemo(() => {
    const availableOptions = options ?? Object.values(CarTypeFilter);
    return availableOptions.flatMap((c) => {
      const label = getLabel(c);
      return label != null
        ? [
            {
              label: label,
              value: c,
            },
          ]
        : [];
    });
  }, [getLabel, options]);

  return {
    items,
    options,
    selectedValues,
    onChange,
    getLabel,
  };
};
